import { CoreApplicationContext } from '@eventbrite/context-gen';
import { Layout } from '@eventbrite/eds-layout';
import { gettext, setLanguage } from '@eventbrite/i18n';
import { useRealUserMonitoring } from '@eventbrite/real-user-monitoring';
import { SiteStructure } from '@eventbrite/site-structure';
import { deepKeysToCamel } from '@eventbrite/transformation-utils';
import { useConstructor } from '@eventbrite/use-constructor';
import { WagtailToolbar } from '@eventbrite/wagtail-toolbar';
import * as Sentry from '@sentry/react';
import React from 'react';
import './app.scss';
import ModuleList, { ModuleInterface } from './components/ModuleList';

export interface AppProps {
    modules: {
        body_content: [ModuleInterface];
        body_header?: any;
    };
    env: any;
    user: any;
    request: any;
    wagtail_toolbar?: {
        edit_page_link: string;
        login_link: string;
    };
    include_footer: boolean;
    footerLinks: any;
    should_use_webpack_template: boolean;
    app_version: string;
    app_name: string;
}

export const App: React.FunctionComponent<
    AppProps & CoreApplicationContext
> = ({
    modules,
    env,
    user,
    request,
    wagtail_toolbar,
    include_footer,
    footerLinks,
    should_use_webpack_template,
    app_version,
    app_name,
}) => {
    useConstructor(() => {
        setLanguage(env.localeInfo.locale);
    });
    // Only send samples to datadog RUM if it's production
    const sampleRate = env.ebDomain.indexOf('eventbrite') > -1 ? 5 : 0;
    useRealUserMonitoring({
        sampleRate,
        version: app_version,
        service: app_name,
    });

    const moduleData = (
        <section className="content-container">
            <ModuleList
                env={env}
                user={user}
                modules={deepKeysToCamel(modules['body_content'])}
            />
        </section>
    );
    if (!should_use_webpack_template) {
        return moduleData;
    }

    const linksForFooter = include_footer ? footerLinks : null;
    let wagtailToolbar = null;

    if (wagtail_toolbar) {
        const toolbar = deepKeysToCamel(wagtail_toolbar);
        wagtailToolbar = toolbar && (
            <WagtailToolbar
                editPageLink={toolbar.editPageLink}
                loginLink={toolbar.loginLink}
            />
        );
    }

    return (
        /* Sitestructure has an error boundary built in around its children, so errors that sentry
        should be grabbing are potentially being swallowed. This ensures that the error is caught
        and not piped to the default error system. */
        <Sentry.ErrorBoundary
            fallback={<>{gettext('An error has occurred')}</>}
        >
            {wagtailToolbar}
            <SiteStructure
                env={env}
                user={user}
                request={request}
                fullScreenMain={true}
                footerLinks={linksForFooter}
                showMarketingLinks={!user.isAuthenticated}
            >
                <Layout>{moduleData}</Layout>
            </SiteStructure>
        </Sentry.ErrorBoundary>
    );
};
